import { isTokenExpired } from '../utils/jwt';
import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    const url = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
    this.baseURL = url.endsWith('/api') ? url : `${url}/api`;
  }

  private handleUnauthorized(): void {
    // Token expired or invalid, logout user
    authService.logout();
    // Redirect to login page
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const token = localStorage.getItem('token');

    // Check if token is expired before making request
    if (token && isTokenExpired(token)) {
      console.warn('Token expired, logging out...');
      this.handleUnauthorized();
      throw new Error('Token expired');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      // Handle 401 Unauthorized
      if (response.status === 401) {
        console.warn('Unauthorized request, token may be expired');
        this.handleUnauthorized();
        throw new Error('Unauthorized: Please login again');
      }

      // Handle 403 Forbidden
      if (response.status === 403) {
        const apiError = new Error('Erişim Reddedildi');
        (apiError as any).response = {
          data: {
            message: 'Bu işlem için gerekli yetkilere sahip değilsiniz.',
          },
          status: 403,
        };
        throw apiError;
      }

      // Try to parse error response as JSON
      const contentType = response.headers.get('content-type');
      const hasJsonContent =
        contentType && contentType.includes('application/json');

      if (hasJsonContent) {
        try {
          const error = await response.json();
          // Replace "Forbidden resource" with Turkish message
          let errorMessage =
            error.message ||
            error.error ||
            `HTTP error! status: ${response.status}`;

          if (
            errorMessage.toLowerCase().includes('forbidden') ||
            errorMessage.toLowerCase().includes('forbidden resource')
          ) {
            errorMessage = 'Erişim Reddedildi';
          }

          const apiError = new Error(errorMessage);
          (apiError as any).response = { data: error, status: response.status };
          throw apiError;
        } catch (e) {
          if (e instanceof Error && e.message.includes('JSON')) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          throw e;
        }
      } else {
        throw new Error(
          `HTTP error! status: ${response.statusText || response.status}`,
        );
      }
    }

    // Handle 204 No Content - no response body
    if (response.status === 204) {
      return undefined as T;
    }

    // Check if response has content
    const contentType = response.headers.get('content-type');
    const hasJsonContent =
      contentType && contentType.includes('application/json');

    if (!hasJsonContent) {
      // If no JSON content type, return undefined for successful responses
      return undefined as T;
    }

    // Try to parse JSON
    const text = await response.text();
    if (!text || text.trim() === '') {
      return undefined as T;
    }

    try {
      return JSON.parse(text);
    } catch (e) {
      // If JSON parsing fails but response is OK, return undefined
      if (response.ok) {
        console.warn('Failed to parse JSON response, returning undefined');
        return undefined as T;
      }
      throw new Error('Invalid JSON response');
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
